import { useEffect, useMemo, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Cipher Breaker (level 20) — assign each enciphered word the dictionary term it stands for.
// A substitution preserves a word's SHAPE (length + where letters repeat), so no English
// vocabulary is needed: match shapes, then find the one letter mapping that explains every word
// at once. Checks report how many are right, never which.
//
// The shape-compatible candidates and the recovered letters both come from the server, so this
// component never computes anything the grader could disagree with. The substitution and the
// answer stay server-side.

interface Check {
  answers: string[];
  correct: number;
}

interface Challenge {
  cipherWords: string[];
  dictionary: string[];
  /** Cipher letter → plain letter, for the letters the toolchain recovered. */
  revealed: Record<string, string>;
  checksLeft: number;
  checksGranted: number;
  checkHistory: Check[];
  showFilter: boolean;
  /** Per enciphered word, the shape-compatible terms. Empty without the tool. */
  candidates: string[][];
  timeLimitSec: number;
}

/** A word's shape, for display: A B C for distinct letters, repeats reusing the same tag. */
function shapeOf(word: string): string {
  return word.split('').map(c => String.fromCharCode(65 + word.indexOf(c))).join('');
}

export default function CipherBreakerView({
  challenge, onAct, onSubmit, setPendingSubmission, busy,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [picks, setPicks] = useState<(string | null)[]>([]);
  const [slot, setSlot] = useState(0);
  const [note, setNote] = useState<string | null>(null);

  const cipherWords = useMemo(() => live?.cipherWords ?? [], [live]);
  const dictionary = useMemo(() => live?.dictionary ?? [], [live]);
  // Memoised: the fallback would be a fresh object every render and re-fire the memo below.
  const revealed = useMemo(() => live?.revealed ?? {}, [live]);

  useEffect(() => {
    setLive(challenge ?? null);
    const n = challenge?.cipherWords?.length ?? 0;
    setPicks(Array(n).fill(null));
    setSlot(0);
    setNote(null);
    setPendingSubmission({ answers: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge]);

  const complete = picks.length > 0 && picks.every(p => p !== null);

  const assign = (term: string) => {
    if (busy) return;
    // A term is used at most once: taking it frees wherever it was.
    const next = picks.map(p => (p === term ? null : p));
    next[slot] = term;
    setPicks(next);
    setNote(null);
    if (next.every(p => p !== null)) setPendingSubmission({ answers: next });
    else setPendingSubmission({ answers: [] });
    // Move to the next still-empty slot, so a run of taps fills the board.
    const empty = next.findIndex((p, i) => p === null && i > slot);
    setSlot(empty >= 0 ? empty : next.findIndex(p => p === null) >= 0 ? next.findIndex(p => p === null) : slot);
  };

  const clearSlot = (i: number) => {
    if (busy) return;
    const next = [...picks];
    next[i] = null;
    setPicks(next);
    setSlot(i);
    setPendingSubmission({ answers: [] });
  };

  const check = async () => {
    if (busy || !complete || (live?.checksLeft ?? 0) <= 0) return;
    setNote(null);
    const next = await onAct({ answers: picks as string[] });
    if (next) {
      setLive(next);
      const last = next.checkHistory?.[next.checkHistory.length - 1];
      if (last) setNote(`${last.correct} of ${cipherWords.length} in the right place — the rest are wrong.`);
    }
  };

  /** The letter mapping the current assignment implies, plus whether it contradicts itself. */
  const implied = useMemo(() => {
    const map: Record<string, string> = { ...revealed };
    const back: Record<string, string> = {};
    Object.entries(revealed).forEach(([c, p]) => { back[p] = c; });
    let conflict = false;

    picks.forEach((term, i) => {
      if (!term) return;
      const cipher = cipherWords[i] ?? '';
      for (let k = 0; k < cipher.length && k < term.length; k++) {
        const c = cipher[k], p = term[k];
        if (map[c] !== undefined && map[c] !== p) conflict = true;
        else if (back[p] !== undefined && back[p] !== c) conflict = true;
        else { map[c] = p; back[p] = c; }
      }
    });
    return { map, conflict };
  }, [picks, cipherWords, revealed]);

  const cell: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };

  return (
    <div style={{ fontFamily: 'monospace' }}>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', margin: '0 0 0.75rem' }}>
        One letter always becomes the same other letter. You don't need to know the words: match
        each cipher word's <strong style={{ color: '#cbd5e1' }}>shape</strong> (its length and where
        letters repeat), then find the single mapping that fits <strong>all</strong> of them.
      </p>

      {/* The intercepted transmission */}
      <div style={{ display: 'grid', gap: '0.4rem', marginBottom: '0.9rem' }}>
        {cipherWords.map((word, i) => {
          const cands = live?.candidates?.[i] ?? [];
          const active = i === slot;
          return (
            <div key={i} style={{
              border: `1px solid ${active ? 'var(--primary-color)' : '#232833'}`,
              borderRadius: 4, padding: '0.45rem 0.6rem', background: active ? '#12161d' : '#0d1117',
            }}>
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
                <button
                  onClick={() => setSlot(i)}
                  disabled={busy}
                  style={{
                    ...cell, fontSize: '1.05rem', fontWeight: 'bold', letterSpacing: '0.12em',
                    padding: '0.1rem 0.4rem', color: '#e2e8f0',
                  }}
                  title="Work on this word"
                >
                  {word}
                </button>
                <span style={{ ...cell, color: 'var(--text-muted)', fontSize: '0.7rem' }}>
                  shape {shapeOf(word)}
                </span>
                {picks[i] ? (
                  <span style={{ ...cell, color: '#4ade80' }}>
                    → {picks[i]}
                    <button
                      onClick={() => clearSlot(i)}
                      disabled={busy}
                      style={{ marginLeft: '0.4rem', fontSize: '0.68rem', padding: '0 0.35rem' }}
                    >
                      clear
                    </button>
                  </span>
                ) : (
                  <span style={{ ...cell, color: '#4b5563' }}>→ unassigned</span>
                )}
              </div>
              {live?.showFilter && cands.length > 0 && (
                <div style={{ ...cell, color: '#6b7280', fontSize: '0.68rem', marginTop: '0.2rem' }}>
                  same shape: {cands.join(', ')}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* The dictionary */}
      <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.3rem' }}>
        dictionary — tap a term to place it in the highlighted word
      </div>
      <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginBottom: '0.9rem' }}>
        {dictionary.map(term => {
          const usedAt = picks.indexOf(term);
          const fits = !live?.showFilter || (live?.candidates?.[slot] ?? []).includes(term);
          return (
            <button
              key={term}
              onClick={() => assign(term)}
              disabled={busy || !fits}
              title={fits ? undefined : "this term's shape doesn't match the selected word"}
              style={{
                fontSize: '0.72rem', padding: '0.25rem 0.5rem',
                color: usedAt >= 0 ? '#4ade80' : undefined,
                borderColor: usedAt >= 0 ? '#166534' : undefined,
              }}
            >
              {term}{usedAt >= 0 && <span style={{ color: 'var(--text-muted)' }}> #{usedAt + 1}</span>}
            </button>
          );
        })}
      </div>

      {/* The key as the current assignment implies it */}
      <div style={{ border: '1px solid #232833', borderRadius: 4, padding: '0.45rem 0.6rem', marginBottom: '0.8rem' }}>
        <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
          key so far{Object.keys(revealed).length > 0 && ` — ${Object.keys(revealed).length} letter(s) recovered by your toolchain`}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {Object.keys(implied.map).length === 0
            ? <span style={{ ...cell, color: '#4b5563' }}>nothing pinned down yet</span>
            : Object.entries(implied.map).sort(([a], [b]) => a.localeCompare(b)).map(([c, p]) => (
              <span key={c} style={{
                ...cell, fontSize: '0.74rem',
                color: revealed[c] ? '#60a5fa' : '#cbd5e1',
              }}>
                {c}→{p}
              </span>
            ))}
        </div>
        {implied.conflict && (
          <div style={{ ...cell, color: '#f87171', fontSize: '0.72rem', marginTop: '0.25rem' }}>
            these terms can't all be right — one letter would need two meanings
          </div>
        )}
      </div>

      {/* Checks already spent */}
      {(live?.checkHistory?.length ?? 0) > 0 && (
        <div style={{ marginBottom: '0.8rem' }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '0.2rem' }}>
            checks spent
          </div>
          {live!.checkHistory.map((h, i) => (
            <div key={i} style={{ ...cell, fontSize: '0.72rem', color: '#94a3b8' }}>
              {h.answers.join(' ')} — <strong style={{ color: h.correct > 0 ? '#fbbf24' : '#f87171' }}>{h.correct}</strong> in place
            </div>
          ))}
        </div>
      )}

      {note && (
        <p className="fx-fade-in" style={{ ...cell, color: '#fbbf24', fontSize: '0.78rem', margin: '0 0 0.6rem' }}>{note}</p>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          className="primary"
          onClick={() => onSubmit({ answers: picks })}
          disabled={busy || !complete}
          style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}
        >
          Decrypt
        </button>
        <button
          onClick={check}
          disabled={busy || !complete || (live?.checksLeft ?? 0) <= 0}
          style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
          title="Costs one check and tells you how many words are in the right place — not which"
        >
          Check ({live?.checksLeft ?? 0} left)
        </button>
        <span style={{ ...cell, color: 'var(--text-muted)', fontSize: '0.74rem' }}>
          each check spent costs a little of the reward
        </span>
      </div>
    </div>
  );
}
